import { useState, ReactNode } from 'react';

import { cn } from '@/shadcn/lib/utils';
import { ChevronDown } from 'lucide-react';

type Props = { title?: string; children: ReactNode; dataCyName?: string } & (
  | {
      isCollapsable: true;
      isCollapsed?: boolean;
      onToggleCollapse?: () => void;
    }
  | {
      isCollapsable?: false;
      isCollapsed?: boolean;
      onToggleCollapse?: () => void;
    }
);

export const KeyValue = (props: Props) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    props.isCollapsed ?? false
  );

  const onToggleCollapse = () => {
    if (!props.isCollapsable) return;
    setIsCollapsed(prev => !prev);
    props.onToggleCollapse?.();
  };

  return (
    <div
      className="group flex w-full flex-col flex-nowrap gap-[5px]"
      data-testid="key-value-wrapper"
      data-cy={`key-value-wrapper-${props.dataCyName || props.title}`}
      data-is-collapsed={isCollapsed}
    >
      <div
        className={cn(
          'text-primary-deep group/title flex items-center',
          props.isCollapsable && 'cursor-pointer hover:brightness-90',
          !props.title && 'hidden'
        )}
        onClick={onToggleCollapse}
        data-testid="key-value-title-container"
        data-cy={`key-value-title-container-${props.dataCyName || props.title}`}
        aria-hidden={!props.title}
        aria-label={
          props.isCollapsable ? 'Click to toggle collapse' : undefined
        }
      >
        <div
          className="truncate text-[10px] leading-[12px] font-semibold tracking-[0.5px] uppercase"
          title={props.title}
          data-testid="key-value-title"
        >
          {props.title}
        </div>
        {props.isCollapsable && (
          <div
            className={cn(
              'ml-1 flex h-3 w-3 items-center justify-between transition-transform duration-300 ease-out group-hover/title:rotate-180',
              isCollapsed && 'rotate-180'
            )}
            data-testid="key-value-arrow-icon"
          >
            <ChevronDown size={14} />
          </div>
        )}
      </div>
      <div
        className={cn(
          'border-border text-card-foreground group-hover:border-primary ml-2 flex min-h-[18px] flex-row flex-nowrap items-center truncate border-l-2 pl-4 text-sm text-wrap',
          isCollapsed && 'hidden'
        )}
        aria-hidden={isCollapsed}
        data-testid="key-value-content-container"
        data-cy={`key-value-content-container-${props.dataCyName || props.title}`}
      >
        {props.children}
      </div>
    </div>
  );
};
